import { supabase } from '../lib/supabase';
import paykuConfig from '../config/payku';

// Configuración de Payku
const PAYKU_API_URL = import.meta.env.VITE_PAYKU_API_URL || 'https://app.payku.cl/api';
const PAYKU_PUBLIC_TOKEN = import.meta.env.VITE_PAYKU_PUBLIC_TOKEN;
const PAYKU_PRIVATE_TOKEN = import.meta.env.VITE_PAYKU_PRIVATE_TOKEN;

// Log de la configuración para debugging (remover en producción)
console.log('PAYKU CONFIG:', {
  url: PAYKU_API_URL,
  publicTokenLength: PAYKU_PUBLIC_TOKEN ? PAYKU_PUBLIC_TOKEN.length : 0,
  privateTokenLength: PAYKU_PRIVATE_TOKEN ? PAYKU_PRIVATE_TOKEN.length : 0
});

// Definición de tipos
export interface PaykuTransactionResponse {
  id: string;
  status: string;
  order: string;
  subject: string;
  amount: number;
  currency: string;
  email: string;
  payment_url?: string;  // Puede no estar presente en todas las respuestas
  url?: string;          // URL alternativa en algunas respuestas
  return_url: string;
  cancel_url: string;
  notify_url: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface PaykuPackage {
  id: string;
  name: string;
  description: string;
  amount: number;
  vcoins: number;
  currency: string;
}

// Paquetes disponibles - Soporta CLP y USD
// NOTA: Payku muestra precios en USD pero convierte automáticamente a CLP al momento del pago
export const PAYKU_PACKAGES: PaykuPackage[] = [
  // Paquetes en CLP (Chile)
  {
    id: 'basic',
    name: 'Starter',
    description: '1 Coin para generar 1 CV optimizado',
    amount: 3990,
    vcoins: 1,
    currency: 'CLP'
  },
  {
    id: 'standard',
    name: 'Paquete Estándar',
    description: '3 Coins para generar 3 CVs optimizados',
    amount: 7490,
    vcoins: 3,
    currency: 'CLP'
  },
  {
    id: 'premium',
    name: 'Advanced',
    description: '10 Coins para generar 10 CVs optimizados - MEJOR OFERTA',
    amount: 9990,
    vcoins: 10,
    currency: 'CLP'
  },
  // Paquetes en USD (Internacional)
  {
    id: 'basic-usd',
    name: 'Starter',
    description: '1 Coin to generate 1 optimized CV',
    amount: 4.99,
    vcoins: 1,
    currency: 'USD'
  },
  {
    id: 'standard-usd',
    name: 'Standard Package',
    description: '3 Coins to generate 3 optimized CVs',
    amount: 8.99,
    vcoins: 3,
    currency: 'USD'
  },
  {
    id: 'premium-usd',
    name: 'Advanced',
    description: '10 Coins to generate 10 optimized CVs - BEST VALUE',
    amount: 11.99,
    vcoins: 10,
    currency: 'USD'
  }
];

// Add interface for transaction verification results
interface TransactionVerificationResult {
  success: boolean;
  error?: string;
  message: string;
  details?: any;
}

export class PaykuService {
  private static instance: PaykuService;
  private baseUrl: string;
  private publicToken: string;
  private privateToken: string;

  private constructor() {
    this.baseUrl = PAYKU_API_URL;
    this.publicToken = PAYKU_PUBLIC_TOKEN;
    this.privateToken = PAYKU_PRIVATE_TOKEN;
  }

  public static getInstance(): PaykuService {
    if (!PaykuService.instance) {
      PaykuService.instance = new PaykuService();
    }
    return PaykuService.instance;
  }

  // Crear una nueva transacción de pago
  public async createTransaction(
    userId: string,
    packageId: string,
    amount: number,
    coinsAmount: number,
    currency: 'CLP' | 'USD' = 'CLP'
  ): Promise<{
    success: boolean;
    message: string;
    payment_url?: string;
    transaction_id?: string
  }> {
    try {
      console.log(`Creando transacción para usuario ${userId}, paquete ${packageId}, monto ${amount}`);

      // Generar un ID único para la transacción
      const txId = crypto.randomUUID();

      // Guardar información de la transacción en sessionStorage y localStorage
      const transactionData = {
        id: txId,
        package_id: packageId,
        amount: amount,
        coins: coinsAmount,
        timestamp: Date.now(),
        user_id: userId
      };

      // Guardar en localStorage y sessionStorage para mayor persistencia
      localStorage.setItem('pendingTransaction', JSON.stringify(transactionData));
      sessionStorage.setItem('pendingTransaction', JSON.stringify(transactionData));

      // Guardar el último ID de transacción para verificación posterior
      localStorage.setItem('last_payku_transaction', txId);
      // Guardar en formato estándar como en la documentación
      localStorage.setItem('last_payku_time', new Date().toISOString());
      localStorage.setItem('last_payku_details', JSON.stringify({
        id: txId,
        pack: packageId,
        amount: amount,
        vcoins: coinsAmount,
        timestamp: new Date().toISOString(),
        order_id: txId
      }));

      // IMPORTANTE: Payku SOLO acepta amounts como INTEGER
      // Si es USD, convertir a CLP primero
      let paykuAmount = amount;
      let paykuCurrency = currency;

      if (currency === 'USD') {
        // Convertir USD a CLP (1 USD ≈ 865 CLP)
        paykuAmount = Math.round(amount * 865);
        paykuCurrency = 'CLP'; // Payku siempre procesa en CLP
        console.log(`[PAYKU] Convirtiendo ${amount} USD a ${paykuAmount} CLP para Payku`);
      } else {
        // CLP ya es integer, asegurar que sea entero
        paykuAmount = Math.round(amount);
      }

      // Guardar la transacción en la base de datos con el monto en CLP (INTEGER)
      await this.saveTransactionToDatabase(userId, packageId, amount, coinsAmount, txId, currency, paykuAmount);

      // Crear la transacción en Payku
      const url = `${this.baseUrl}/transaction`;
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.publicToken}`
      };

      // Obtener el email del usuario si está disponible
      let userEmail = 'cliente@example.com';
      let userName = 'Usuario CV Maker AI';
      try {
        // Usar supabase.auth en lugar de la tabla users
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (!userError && user && user.email) {
          userEmail = user.email;
          // Intentar obtener el nombre del usuario si está disponible
          const { data: userData, error: profileError } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();

          if (!profileError && userData) {
            if (userData.first_name || userData.last_name) {
              userName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
            }
          }
        }
      } catch (error) {
        console.error('Error al obtener datos del usuario:', error);
      }

      // Determinar el entorno (desarrollo o producción)
      const isDevelopment = window.location.hostname === 'localhost';
      const baseUrl = isDevelopment
        ? window.location.origin
        : 'https://curvia.app'; // Cambiado a tu dominio de producción

      // Configurar URLs siguiendo la documentación
      const returnUrl = `${baseUrl}/payment/return?source=payku&txid=${txId}`;
      const cancelUrl = `${baseUrl}/account`;
      const notifyUrl = `${baseUrl}/api/payment/notify`;

      // Crear el cuerpo de la solicitud con las URLs correctas y datos del usuario
      const orderInfo = `VCoins-${packageId}-${coinsAmount}`;
      const body = {
        // Valores requeridos por Payku
        order: txId,
        subject: `Compra de ${coinsAmount} VCoins - ${packageId}`,
        amount: paykuAmount, // ← SIEMPRE INTEGER, en CLP
        currency: paykuCurrency, // ← SIEMPRE CLP para Payku
        email: userEmail,
        full_name: userName,
        // URLs de redirección
        urlreturn: returnUrl,
        urlcancel: cancelUrl,
        urlnotify: notifyUrl,
        // Valores opcionales
        timeout: 30, // Tiempo en minutos antes de que expire la transacción
        payment_method: '' // Dejamos vacío para permitir todos los métodos
      };

      console.log('Enviando solicitud a Payku:', {
        ...body,
        url: url,
        headers: { ...headers, Authorization: 'Bearer ***' } // Ocultar token en logs
      });

      // Guardar la URL de retorno en localStorage para poder redirigir manualmente si es necesario
      localStorage.setItem('payku_return_url', returnUrl);
      localStorage.setItem('payku_cancel_url', cancelUrl);
      localStorage.setItem('payku_transaction', JSON.stringify({
        transaction_id: txId,
        package_id: packageId,
        amount: amount,
        vcoins_amount: coinsAmount,
        created_at: new Date().toISOString()
      }));

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error en respuesta de Payku:', response.status, errorText);
        return {
          success: false,
          message: `Error al crear la transacción: ${response.status} ${response.statusText}`
        };
      }

      const data = await response.json();
      console.log('Respuesta de Payku:', data);

      // Extraer la URL de pago y el ID de transacción DE PAYKU de la respuesta
      let paymentUrl = data.url || data.payment_url || '';
      let paykuTransactionId = data.id;

      if (!paymentUrl) {
        console.error('No se encontró URL de pago en la respuesta de Payku:', data);
        return { success: false, message: 'Error: No se recibió URL de pago.' };
      }
      if (!paykuTransactionId) {
        console.error('No se encontró ID de transacción de Payku en la respuesta:', data);
        // Podríamos continuar sin él, pero la verificación posterior fallará.
        // Mejor retornar error.
        return { success: false, message: 'Error: No se recibió ID de transacción de Payku.' };
      }

      // Actualizar la transacción en la base de datos con el ID de Payku
      const updateSuccess = await this.updateTransactionWithPaykuId(txId, paykuTransactionId, paymentUrl);

      if (!updateSuccess) {
        console.error(`[PAYKU] Falló la actualización de la BD para ${txId} con el ID de Payku. La verificación posterior probablemente fallará.`);
        // Aunque Payku creó la transacción, consideramos esto un fallo del flujo completo
        // porque no podemos rastrear el pago correctamente.
        return {
          success: false,
          message: 'Error: La transacción se creó en Payku, pero falló la actualización en la base de datos local.'
        };
      }

      return {
        success: true,
        message: '¡Transacción creada correctamente!',
        payment_url: paymentUrl,
        transaction_id: paykuTransactionId
      };
    } catch (error) {
      console.error('Error inesperado al crear la transacción:', error);
      return { success: false, message: 'Error inesperado en el servidor.' };
    }
  }

  // Guarda la transacción inicial (antes de llamar a Payku)
  private async saveTransactionToDatabase(
    userId: string,
    packageId: string,
    amount: number,
    vcoinsAmount: number,
    internalTxId: string, // Este es TU ID interno (UUID)
    currency: 'CLP' | 'USD' = 'CLP',
    amountInCLP?: number // Monto convertido a CLP para Payku
  ): Promise<boolean> {
    console.log(`Guardando transacción en la base de datos: userId=${userId}, packageId=${packageId}, amount=${amount}, txId=${internalTxId}`);
    try {
      const { data, error } = await supabase
        .from('transactions')
        .insert({
          transaction_id: internalTxId, // Guardar TU ID interno
          user_id: userId,
          amount: amountInCLP || Math.round(amount), // Guardar monto en CLP (INTEGER para compatibilidad)
          vcoins_amount: vcoinsAmount,
          currency: currency, // Usar moneda del paquete seleccionado
          status: 'pending', // Estado inicial
          description: `Compra VCoins - Paquete: ${packageId}`,
          metadata: {
            initial_creation: true,
            package_id: packageId,
            original_amount: amount, // Guardar monto original en metadata
            original_currency: currency, // Guardar moneda original
            amount_in_clp: amountInCLP || Math.round(amount) // Monto convertido
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (error) {
        console.error('Error al guardar la transacción en la base de datos:', error);
        return false;
      }
      console.log('Transacción guardada correctamente en la base de datos');
      return true;
    } catch (error) {
      console.error('Error inesperado al guardar la transacción:', error);
      return false;
    }
  }

  // Nueva función para actualizar la transacción con el ID de Payku y la URL de pago
  private async updateTransactionWithPaykuId(
    internalTxId: string, // Tu UUID
    paykuTxKey: string, // El ID de Payku (trx...)
    paymentUrl: string
  ): Promise<boolean> {
    console.log(`Actualizando transacción ${internalTxId} con ID de Payku ${paykuTxKey} y URL de pago.`);
    try {
      const { error } = await supabase
        .from('transactions')
        .update({
          payku_transaction_key: paykuTxKey, // Guardar el ID de Payku
          payment_url: paymentUrl, // Guardar/Actualizar URL de pago
          updated_at: new Date().toISOString()
        })
        .eq('transaction_id', internalTxId); // Buscar por TU ID interno

      if (error) {
        console.error(`Error al actualizar la transacción ${internalTxId} con el ID de Payku:`, error);
        return false;
      }
      console.log(`Transacción ${internalTxId} actualizada con éxito con ID de Payku.`);
      return true;
    } catch (error) {
      console.error(`Error inesperado al actualizar transacción ${internalTxId} con ID de Payku:`, error);
      return false;
    }
  }

  // Crear la tabla transactions si no existe
  private async createTransactionsTable(): Promise<void> {
    try {
      // Ejecutar SQL para crear la tabla
      const { error } = await supabase.rpc('create_transactions_table');

      if (error) {
        console.error('Error al crear tabla transactions:', error);
      } else {
        console.log('Tabla transactions creada exitosamente');
      }
    } catch (error) {
      console.error('Error al crear tabla transactions:', error);
    }
  }

  // Obtener el estado de una transacción directamente desde Payku
  public async getTransactionStatus(transactionId: string): Promise<{
    success: boolean;
    details?: any;
    error?: string;
    orderId?: string;
    isCompleted?: boolean;
  }> {
    try {
      console.log(`Verificando estado de transacción ${transactionId} directamente con Payku`);

      // Intentar diferentes formatos de ID de transacción si necesario
      let transactionIdToUse = transactionId;

      // Si el ID no comienza con 'trx', intentar añadirlo para compatibilidad con la API
      if (!transactionIdToUse.startsWith('trx') && !transactionIdToUse.includes('_')) {
        transactionIdToUse = `trx${transactionIdToUse}`;
        console.log(`ID de transacción reformateado para API: ${transactionIdToUse}`);
      }

      // Configurar headers para autenticación
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      // Tokens para autenticación
      const publicToken = this.publicToken;
      const privateToken = this.privateToken;

      // Si no hay token privado, intentar buscar la transacción en nuestra DB primero
      if (!privateToken) {
        console.warn('Token privado de Payku no disponible. Verificando transacción localmente.');
        const transactionData = await this.findTransactionInDatabase(transactionId);

        if (transactionData) {
          console.log('Transacción encontrada en base de datos local:', transactionData);
          return {
            success: true,
            details: transactionData,
            orderId: transactionData.transaction_id,
            // Consideramos completada si está marcada como tal en nuestra base de datos
            isCompleted: transactionData.completed === true || transactionData.status === 'success'
          };
        }
      }

      let response = null;
      let authMethod = '';
      let error = null;

      // Estrategia 1: Usar token privado como Bearer
      if (privateToken) {
        try {
          console.log('Intentando autenticación con Bearer + privateToken');
          authMethod = 'privateBearer';
          headers['Authorization'] = `Bearer ${privateToken}`;

          response = await fetch(`${this.baseUrl}/transaction/${transactionIdToUse}`, {
            method: 'GET',
            headers
          });

          if (response.ok) {
            const data = await response.json();
            console.log('Respuesta obtenida con método privateBearer:', data);

            // Almacenar los detalles para recuperación posterior
            sessionStorage.setItem('payku_transaction_details', JSON.stringify(data));

            return {
              success: true,
              details: data,
              orderId: data.order,
              isCompleted: data.status === 'success'
            };
          } else {
            console.error(`Error con método privateBearer: ${response.status} ${response.statusText}`);
          }
        } catch (err) {
          error = err;
          console.error('Error con método privateBearer:', err);
        }
      }

      // Estrategia 2: Usar token público como Bearer
      if (!response || !response.ok) {
        try {
          console.log('Intentando autenticación con Bearer + publicToken');
          authMethod = 'publicBearer';
          headers['Authorization'] = `Bearer ${publicToken}`;

          response = await fetch(`${this.baseUrl}/transaction/${transactionIdToUse}`, {
            method: 'GET',
            headers
          });

          if (response.ok) {
            const data = await response.json();
            console.log('Respuesta obtenida con método publicBearer:', data);

            // Almacenar los detalles para recuperación posterior
            sessionStorage.setItem('payku_transaction_details', JSON.stringify(data));

            return {
              success: true,
              details: data,
              orderId: data.order,
              isCompleted: data.status === 'success'
            };
          } else {
            console.error(`Error con método publicBearer: ${response.status} ${response.statusText}`);
          }
        } catch (err) {
          error = err;
          console.error('Error con método publicBearer:', err);
        }
      }

      // Estrategia 3: Usar autenticación básica
      if ((!response || !response.ok) && publicToken && privateToken) {
        try {
          console.log('Intentando autenticación con Basic Auth');
          authMethod = 'basicAuth';
          const authString = `${publicToken}:${privateToken}`;
          const base64Auth = btoa(authString);
          headers['Authorization'] = `Basic ${base64Auth}`;

          response = await fetch(`${this.baseUrl}/transaction/${transactionIdToUse}`, {
            method: 'GET',
            headers
          });

          if (response.ok) {
            const data = await response.json();
            console.log('Respuesta obtenida con método basicAuth:', data);

            // Almacenar los detalles para recuperación posterior
            sessionStorage.setItem('payku_transaction_details', JSON.stringify(data));

            return {
              success: true,
              details: data,
              orderId: data.order,
              isCompleted: data.status === 'success'
            };
          } else {
            console.error(`Error con método basicAuth: ${response.status} ${response.statusText}`);
          }
        } catch (err) {
          error = err;
          console.error('Error con método basicAuth:', err);
        }
      }

      // Como último recurso, buscar información almacenada localmente
      console.log('Intentando recuperar información de sessionStorage y localStorage');

      // Intentar recuperar de sessionStorage
      try {
        const storedDetails = sessionStorage.getItem('payku_transaction_details');
        if (storedDetails) {
          const data = JSON.parse(storedDetails);
          if (data && (data.id === transactionId || data.order === transactionId)) {
            console.log('Información de transacción encontrada en sessionStorage:', data);
            return {
              success: true,
              details: data,
              orderId: data.order,
              isCompleted: data.status === 'success'
            };
          }
        }

        // Verificar en payku_transaction
        const storedTxData = localStorage.getItem('payku_transaction');
        if (storedTxData) {
          const txData = JSON.parse(storedTxData);
          if (txData && (txData.transaction_id === transactionId || txData.id === transactionId)) {
            console.log('Información de transacción encontrada en localStorage (payku_transaction):', txData);
            return {
              success: true,
              details: txData,
              orderId: txData.transaction_id || txData.id,
              // No podemos determinar si está completada desde localStorage
              isCompleted: false
            };
          }
        }

        // Verificar en last_payku_details
        const lastDetails = localStorage.getItem('last_payku_details');
        if (lastDetails) {
          const data = JSON.parse(lastDetails);
          if (data && (data.id === transactionId || data.order_id === transactionId)) {
            console.log('Información de transacción encontrada en localStorage (last_payku_details):', data);
            return {
              success: true,
              details: data,
              orderId: data.order_id || data.id,
              isCompleted: false
            };
          }
        }
      } catch (storageError) {
        console.error('Error al recuperar datos de almacenamiento local:', storageError);
      }

      // Como último recurso, buscar en nuestra base de datos
      const dbTransaction = await this.findTransactionInDatabase(transactionId);
      if (dbTransaction) {
        console.log('Transacción encontrada en base de datos:', dbTransaction);
        return {
          success: true,
          details: dbTransaction,
          orderId: dbTransaction.transaction_id,
          isCompleted: dbTransaction.status === 'success' || dbTransaction.status === 'completed'
        };
      }

      console.error('No se pudo obtener información de la transacción por ningún método');
      return {
        success: false,
        error: 'No se pudo obtener información de la transacción',
        details: { last_error: error }
      };
    } catch (error) {
      console.error('Error al obtener estado de transacción:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error desconocido',
        details: { error }
      };
    }
  }

  // Método auxiliar para buscar una transacción en nuestra base de datos
  private async findTransactionInDatabase(transactionId: string): Promise<any | null> {
    try {
      console.log(`Buscando transacción ${transactionId} en la base de datos local`);

      // 1. Búsqueda exacta
      let { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('transaction_id', transactionId)
        .maybeSingle();

      if (data) {
        console.log('Transacción encontrada con ID exacto:', data);
        return data;
      }

      // 2. Búsqueda por LIKE parcial
      const { data: likeData, error: likeError } = await supabase
        .from('transactions')
        .select('*')
        .ilike('transaction_id', `%${transactionId}%`)
        .order('created_at', { ascending: false })
        .limit(1);

      if (likeData && likeData.length > 0) {
        console.log('Transacción encontrada con búsqueda parcial:', likeData[0]);
        return likeData[0];
      }

      // 3. Búsqueda en metadatos
      const { data: recentTransactions } = await supabase
        .from('transactions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (recentTransactions && recentTransactions.length > 0) {
        // Buscar en metadatos de transacciones recientes
        for (const tx of recentTransactions) {
          if (tx.metadata && typeof tx.metadata === 'object') {
            const meta = tx.metadata;
            if (
              (meta.payku_id && meta.payku_id === transactionId) ||
              (meta.transaction_id && meta.transaction_id === transactionId) ||
              (meta.txid && meta.txid === transactionId) ||
              (meta.order_id && meta.order_id === transactionId) ||
              (meta.order && meta.order === transactionId)
            ) {
              console.log('Transacción encontrada en metadatos:', tx);
              return tx;
            }
          }
        }
      }

      console.log(`No se encontró la transacción ${transactionId} en la base de datos local`);
      return null;
    } catch (error) {
      console.error('Error al buscar transacción en la base de datos:', error);
      return null;
    }
  }

  // Verificar una transacción y acreditar monedas si corresponde
  public async verifyTransaction(transactionId: string): Promise<TransactionVerificationResult> {
    try {
      console.log(`[PAYKU] Iniciando verificación de transacción: ${transactionId}`);

      // VERIFICACIÓN NUEVA: Primero comprobar si el pago fue cancelado
      const wasCancelled = await this.checkPaymentWasCancelled(transactionId);
      if (wasCancelled) {
        console.log(`[PAYKU] La transacción ${transactionId} fue cancelada, bloqueando proceso de acreditación`);
        return {
          success: false,
          error: 'TRANSACTION_CANCELLED',
          message: 'La transacción fue cancelada por el usuario o por Payku'
        };
      }

      // Continuar con el proceso normal de verificación...
      // Primero verificar el estado con Payku directamente
      const paykuStatus = await this.getTransactionStatus(transactionId);
      console.log(`Estado de Payku para transacción ${transactionId}:`, paykuStatus);

      // Buscar la transacción en la base de datos
      let transaction = null;
      let searchMethod = 'exact';

      // 1. Intentar buscar por ID exacto
      let { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('transaction_id', transactionId)
        .single();

      if (error || !data) {
        console.log(`No se encontró transacción con ID exacto ${transactionId}, probando búsqueda parcial`);
        searchMethod = 'partial';

        // 2. Intentar búsqueda parcial (por si el ID está almacenado con un prefijo o es parte de otro campo)
        const { data: likeData, error: likeError } = await supabase
          .from('transactions')
          .select('*')
          .like('transaction_id', `%${transactionId}%`)
          .order('created_at', { ascending: false })
          .limit(1);

        if (!likeError && likeData && likeData.length > 0) {
          data = likeData[0];
          transaction = data;
          console.log(`Transacción encontrada con búsqueda parcial:`, transaction);
        } else {
          console.log(`No se encontró transacción con búsqueda parcial para ${transactionId}, verificando en metadata`);
          searchMethod = 'metadata';

          // 3. Buscar en el campo de metadata por si el ID está almacenado allí
          const { data: metadataMatches, error: metadataError } = await supabase
            .from('transactions')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(10);

          if (!metadataError && metadataMatches && metadataMatches.length > 0) {
            // Revisar la metadata de cada transacción reciente
            for (const tx of metadataMatches) {
              if (tx.metadata && typeof tx.metadata === 'object') {
                const meta = tx.metadata;
                if (
                  (meta.payku_id && meta.payku_id === transactionId) ||
                  (meta.transaction_id && meta.transaction_id === transactionId) ||
                  (meta.txid && meta.txid === transactionId) ||
                  (meta.order_id && meta.order_id === transactionId) ||
                  (meta.order && meta.order === transactionId)
                ) {
                  transaction = tx;
                  console.log(`Transacción encontrada en metadata:`, transaction);
                  break;
                }
              }
            }
          }
        }
      } else {
        transaction = data;
        console.log(`Transacción encontrada con ID exacto:`, transaction);
      }

      // Si no encontramos la transacción pero Payku dice que es válida, crearla
      if (!transaction && paykuStatus.success && paykuStatus.isCompleted) {
        console.log(`Transacción no encontrada en DB pero confirmada por Payku, creando registro...`);

        // Intentar recuperar detalles desde localStorage o sessionStorage
        let vcoinsAmount = 1; // Valor predeterminado para paquete básico
        let packageId = 'basic';
        let userId = '';

        // Recuperar ID de usuario y detalles del paquete
        try {
          const storedTxData = localStorage.getItem('payku_transaction');
          if (storedTxData) {
            const txData = JSON.parse(storedTxData);
            if (txData.vcoins_amount) {
              vcoinsAmount = txData.vcoins_amount;
            }
            if (txData.package_id) {
              packageId = txData.package_id;
            }
            if (txData.user_id) {
              userId = txData.user_id;
            }
          }

          // Si no hay user_id almacenado, intentar obtenerlo del usuario actual
          if (!userId) {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              userId = user.id;
            }
          }

          // Si tenemos ID de usuario pero no detalles del paquete, usar valores predeterminados
          if (userId && (!packageId || !vcoinsAmount)) {
            packageId = 'basic';
            vcoinsAmount = 1;
          }
        } catch (err) {
          console.error('Error al recuperar datos de localStorage:', err);
        }

        // Crear transacción en la base de datos
        if (userId) {
          const { data: newTx, error: createError } = await supabase
            .from('transactions')
            .insert({
              transaction_id: transactionId,
              user_id: userId,
              amount: paykuStatus.details?.amount || 0,
              vcoins_amount: vcoinsAmount,
              package_id: packageId,
              completed: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              metadata: {
                payku_id: transactionId,
                payku_status: paykuStatus,
                source: 'direct_verification',
                created_from: 'verification_recovery'
              }
            })
            .select()
            .single();

          if (createError) {
            console.error('Error al crear transacción recuperada:', createError);
          } else {
            console.log('Transacción creada correctamente:', newTx);
            transaction = newTx;
          }
        } else {
          console.error('No se pudo crear transacción: falta ID de usuario');
        }
      }

      // Si aún no tenemos una transacción, no podemos continuar
      if (!transaction) {
        return {
          success: false,
          message: `No se encontró la transacción ${transactionId} en la base de datos ni se pudo crear automáticamente.`,
          error: 'TRANSACTION_NOT_FOUND'
        };
      }

      // Si la transacción ya fue completada anteriormente
      if (transaction.completed) {
        return {
          success: true,
          message: `La transacción ${transactionId} ya fue procesada anteriormente.`,
          details: {
            transaction: transaction,
            alreadyCompleted: true
          }
        };
      }

      // Verificar el estado en Payku si aún no lo hemos hecho
      if (!paykuStatus.success) {
        const paykuCheck = await this.getTransactionStatus(transactionId);

        if (!paykuCheck.success) {
          return {
            success: false,
            message: `No se pudo verificar el estado de la transacción con Payku: ${paykuCheck.error}`,
            error: 'PAYKU_VERIFICATION_FAILED'
          };
        }

        // Actualizar paykuStatus con el resultado de la verificación
        Object.assign(paykuStatus, paykuCheck);
      }

      // Si la transacción no está completada según Payku
      if (!paykuStatus.isCompleted) {
        return {
          success: false,
          message: `La transacción ${transactionId} no ha sido completada según Payku.`,
          details: {
            transaction: transaction,
            paykuStatus: paykuStatus
          },
          error: 'TRANSACTION_NOT_COMPLETED'
        };
      }

      // La transacción está completada según Payku, actualizarla en la base de datos
      const { error: updateError } = await supabase
        .from('transactions')
        .update({
          completed: true,
          updated_at: new Date().toISOString(),
          metadata: {
            ...transaction.metadata,
            payku_status: paykuStatus,
            verified_at: new Date().toISOString()
          }
        })
        .eq('transaction_id', transaction.transaction_id);

      if (updateError) {
        console.error('Error al actualizar transacción:', updateError);
        return {
          success: false,
          message: `Error al actualizar la transacción: ${updateError.message}`,
          error: 'UPDATE_FAILED'
        };
      }

      // Determinar la cantidad de monedas a añadir
      let coinsToAdd = 0;
      let packageId = 'basic';

      if (transaction.vcoins_amount) {
        // Si la transacción tiene definida la cantidad de vcoins, usarla
        coinsToAdd = transaction.vcoins_amount;
      } else if (transaction.metadata && transaction.metadata.package_id) {
        // Si no, intentar obtener el paquete de la metadata
        packageId = transaction.metadata.package_id;

        // Obtener detalles del paquete
        const packageDetails = PAYKU_PACKAGES.find(pkg => pkg.id === packageId);
        if (packageDetails) {
          coinsToAdd = packageDetails.vcoins;
        } else {
          // Si no se encuentra el paquete, usar el básico
          coinsToAdd = 1;
        }
      } else {
        // Valor predeterminado si no hay información
        coinsToAdd = 1;
      }

      // Acreditar las monedas al usuario
      const { error: coinsError } = await supabase.rpc('add_coins', {
        p_user_id: transaction.user_id,
        p_amount: coinsToAdd,
        p_transaction_id: transaction.transaction_id,
        p_reason: `Compra de paquete ${packageId || 'basic'}`
      });

      if (coinsError) {
        console.error('Error al acreditar monedas:', coinsError);
        return {
          success: false,
          message: `Error al acreditar monedas: ${coinsError.message}`,
          error: 'COINS_CREDIT_FAILED'
        };
      }

      return {
        success: true,
        message: `La transacción ${transactionId} ha sido verificada y las monedas han sido acreditadas.`,
        details: {
          transaction: transaction,
          coinsAdded: coinsToAdd
        }
      };
    } catch (error) {
      console.error('Error en verificación de transacción:', error);
      return {
        success: false,
        message: `Error durante la verificación: ${(error as Error).message}`,
        error: 'VERIFICATION_EXCEPTION'
      };
    }
  }

  // Obtener todos los paquetes disponibles
  public getPackages(currency?: 'CLP' | 'USD'): PaykuPackage[] {
    // Si se especifica una moneda, filtrar por ella
    // Si no se especifica, retornar todos los paquetes
    if (currency) {
      return PAYKU_PACKAGES.filter(pkg => pkg.currency === currency);
    }
    return PAYKU_PACKAGES;
  }

  // Obtener un paquete específico por ID
  public getPackageById(packageId: string): PaykuPackage | undefined {
    return PAYKU_PACKAGES.find(pkg => pkg.id === packageId);
  }

  // Función para obtener los detalles de un paquete de monedas por su ID
  private getPackageDetails(packageId: string): { id: string; coins: number; price: number; name: string } | undefined {
    const PAYKU_PACKAGES = [
      { id: 'basic', coins: 1, price: 3990, name: 'Starter' },
      { id: 'standard', coins: 3, price: 7490, name: 'Paquete Estándar' },
      { id: 'premium', coins: 10, price: 9990, name: 'Advanced' }
    ];

    return PAYKU_PACKAGES.find(pkg => pkg.id === packageId);
  }

  /**
   * Verifica si un pago fue cancelado/fallido/expirado consultando la API de Payku.
   * Usa el ID interno (UUID) para buscar el ID de Payku (payku_transaction_key) en la BD.
   * @param internalTransactionId ID de transacción a verificar (UUID interno)
   * @returns true si fue cancelado/fallido/expirado, false si no o si hay error
   */
  public async checkPaymentWasCancelled(internalTransactionId: string): Promise<boolean> {
    if (!internalTransactionId) {
      console.warn('[PAYKU] Error en checkPaymentWasCancelled: ID de transacción interno no proporcionado');
      return false;
    }

    console.log(`[PAYKU] Verificando estado de cancelación para transacción interna: ${internalTransactionId}`);

    // --- PASO 1: Obtener el ID de Payku (payku_transaction_key) y estado local desde nuestra BD ---
    let paykuTxKey: string | null = null;
    let localStatus: string | null = null;
    let dbMetadata: any = null; // Para preservar metadata
    const maxRetries = 2; // Número de reintentos
    const retryDelay = 500; // Milisegundos entre reintentos

    // Función helper para la pausa
    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        const { data: dbTx, error: dbError } = await supabase
          .from('transactions')
          .select('status, payku_transaction_key, metadata') // Seleccionar también metadata
          .eq('transaction_id', internalTransactionId) // Buscar por nuestro UUID
          .maybeSingle();

        if (dbError) {
          console.error(`[PAYKU] Error al buscar en BD para ${internalTransactionId} (intento ${attempt}) en checkPaymentWasCancelled:`, dbError);
          // Si el error persiste después de reintentos, asumir que no podemos determinar
          if (attempt > maxRetries) return false;
          await sleep(retryDelay);
          continue; // Reintentar en caso de error de BD
        }

        if (!dbTx) {
          // Si la transacción no existe en la BD, no tiene sentido reintentar.
          console.warn(`[PAYKU] Transacción ${internalTransactionId} no encontrada en BD (intento ${attempt}). No se puede verificar.`);
          return false; // No encontrada, no puede estar cancelada
        }

        localStatus = dbTx.status;
        paykuTxKey = dbTx.payku_transaction_key;
        dbMetadata = dbTx.metadata; // Guardar metadata existente

        // Si el estado local ya es final (no pendiente), el estado es definitivo, retornar basado en eso.
        if (localStatus !== 'pending') {
          const isLocallyFailed = localStatus === 'cancelled' || localStatus === 'failed' || localStatus === 'error' || localStatus === 'expired';
          console.log(`[PAYKU] Transacción ${internalTransactionId} ya tiene estado local final '${localStatus}' (intento ${attempt}). ¿Cancelada/Fallida? ${isLocallyFailed ? 'Sí' : 'No'}`);
          return isLocallyFailed;
        }

        // Si el estado es 'pending' Y YA tenemos la key, ¡listo! Salimos del bucle.
        if (paykuTxKey) {
          console.log(`[PAYKU] payku_transaction_key (${paykuTxKey}) encontrada para ${internalTransactionId} en intento ${attempt}.`);
          break; // Salir del bucle de reintentos, continuar al PASO 2
        }

        // Si es 'pending' pero NO tenemos la key, y aún quedan reintentos...
        if (attempt <= maxRetries) {
          console.warn(`[PAYKU] No se encontró payku_transaction_key para ${internalTransactionId} (estado local: ${localStatus}) en intento ${attempt}. Reintentando en ${retryDelay}ms...`);
          await sleep(retryDelay);
          // Continuar al siguiente intento del bucle
        } else {
          // Último intento fallido para obtener la key mientras estaba pending
          console.error(`[PAYKU] No se encontró payku_transaction_key para ${internalTransactionId} después de ${maxRetries + 1} intentos (estado local: ${localStatus}). No se puede consultar estado en Payku.`);
          return false; // Asumir que no está cancelada si no podemos verificar
        }

      } catch (error) {
        console.error(`[PAYKU] Excepción al buscar en BD para ${internalTransactionId} (intento ${attempt}) en checkPaymentWasCancelled:`, error);
        // Si hay una excepción irrecuperable después de reintentos
        if (attempt > maxRetries) return false;
        await sleep(retryDelay); // Esperar antes de reintentar
      }
    } // Fin del bucle for (reintentos)

    // Si salimos del bucle porque localStatus !== 'pending', ya retornamos dentro del bucle.
    // Si salimos del bucle porque fallaron todos los reintentos, ya retornamos false dentro del bucle.
    // Si llegamos aquí, es porque encontramos paykuTxKey Y localStatus era 'pending'.

    // Ahora, una comprobación de seguridad: ¿realmente tenemos la key?
    if (!paykuTxKey) {
      // Esta situación no debería ocurrir dada la lógica del bucle, pero es una salvaguarda.
      console.error(`[PAYKU] Error lógico: Se intentó proceder al PASO 2 sin un paykuTxKey válido para ${internalTransactionId} (estado local final: ${localStatus})`);
      return false; // No podemos proceder sin la key
    }


    // --- PASO 2: Consultar API de Payku usando el ID de Payku ---
    console.log(`[PAYKU] Consultando API para ${paykuTxKey} (obtenido de ${internalTransactionId})`);

    // Verificar si tenemos tokens configurados en la instancia
    if (!this.publicToken || !this.privateToken) {
      console.warn('[PAYKU] Advertencia: Tokens de Payku no configurados correctamente en instancia de PaykuService');
      return false; // No podemos verificar sin tokens
    }

    // Registro de tokens para debugging (solo longitud por seguridad)
    console.log(`[PAYKU] Usando tokens - Público: ${this.publicToken.length} caracteres, Privado: ${this.privateToken.length} caracteres`);

    try {
      // Construir headers con tokens de autenticación
      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.publicToken}`
      };

      // Obtener estado de pago desde la API de Payku usando paykuTxKey
      const response = await fetch(`${this.baseUrl}/transaction/${paykuTxKey}`, {
        method: 'GET',
        headers
      });

      // Verificar si hay error de autenticación
      if (response.status === 401) {
        console.warn('[PAYKU] Error 401: Autenticación rechazada por Payku API (en checkPaymentWasCancelled)');
        return false; // No podemos determinar el estado real
      }

      // Verificar si solicitud falló por otro motivo
      if (!response.ok) {
        console.error(`[PAYKU] Error al verificar transacción ${paykuTxKey} en API: ${response.status} - ${response.statusText}`);
        return false; // No podemos determinar
      }

      // Procesar respuesta
      const data = await response.json();

      // Verificar estructura de datos y estado real de Payku
      const transactionDetails = data.transaction || data;
      const statusLower = (transactionDetails?.status || transactionDetails?.gateway_response?.status || 'unknown').toLowerCase();

      const isCancelledOrFailed = statusLower === 'cancelled' ||
        statusLower === 'canceled' || // Alias común
        statusLower === 'rejected' ||
        statusLower === 'failed' ||
        statusLower === 'expired';

      console.log(`[PAYKU] Estado de transacción ${paykuTxKey} según API: ${statusLower}, ¿Cancelada/Fallida? ${isCancelledOrFailed ? 'Sí' : 'No'}`);

      // Si fue cancelada/fallida y el estado local era 'pending', actualizar en base de datos
      if (isCancelledOrFailed && localStatus === 'pending') {
        console.log(`[PAYKU] Actualizando transacción ${internalTransactionId} de 'pending' a '${statusLower}' en BD local (vía checkPaymentWasCancelled)`);
        try {
          await supabase
            .from('transactions')
            .update({
              status: statusLower, // Guardar el estado específico reportado
              updated_at: new Date().toISOString(),
              metadata: {
                ...(dbMetadata || {}), // Preservar metadata existente
                payku_api_status_check: statusLower,
                last_checked_at: new Date().toISOString()
              }
            })
            .eq('transaction_id', internalTransactionId); // Actualizar por UUID interno
        } catch (updateError) {
          console.error('[PAYKU] Error al actualizar estado cancelado en BD:', updateError);
          // No retornar false aquí, el estado de Payku es el que manda
        }
      }

      return isCancelledOrFailed; // Retornar si Payku dice que está cancelada/fallida

    } catch (error) {
      console.error('[PAYKU] Error general en checkPaymentWasCancelled durante llamada API:', error);
      return false; // Asumir no cancelada si hay error inesperado
    }
  }

}